<template>
  <div>
    <PageAllow controller="ReturnPurchases" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1 pe-2">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="
                !$route.path.startsWith('/return-purchases-') &&
                $permission.permissionAllow('ReturnPurchases', 'add')
              "
              small
              color="primary"
              href="/#/return-purchase-new"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("returnPurchase.ObjectName")]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="return_purchase_code"
                  v-model="options.return_purchase_code"
                  @text="returnPurchaseCode = $event"
                  :label="$t('returnPurchase.Fields.return_purchase_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="org_stock_in_code"
                  v-model="options.org_stock_in_code"
                  @text="orgStockInCode = $event"
                  :label="$t('returnPurchase.Fields.stock_in_code')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="ext_code"
                  v-model="options.ext_code"
                  @text="extCodeKeyword = $event"
                  :label="$t('returnPurchase.Fields.ext_code')"
                />
              </v-col>
              <!-- 商品 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterials v-model="options.material_ids" returnId />
              </v-col>
              <!-- 仓库中心 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherStocks v-model="stocksSelected" :stock-types="[0]" />
              </v-col>
              <!-- 供应商 -->
              <v-col cols="12" sm="6" md="4">
                <SearcherSuppliers v-model="supplierSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-if="!$route.path.startsWith('/return-purchases-approve')"
                  v-model="options.status"
                  :items="$consts.return_purchase.status"
                  :label="$t('returnPurchase.status.title')"
                />
                <v-sheet v-else class="transparent-background-color">
                <div
                    class="caption grey--text input-chip-group-label"
                    :class="
                      $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                    "
                >
                  {{ $t("ReturnOrder.grantedStatus") }}
                </div>
                <v-chip-group v-model="grantedStatus" column multiple>
                  <v-chip
                      small
                      label
                      filter
                      class="me-1"
                      color="accent"
                      :value="0"
                  >
                    {{ $t("Order.grantedStatus.confirmed") }}
                  </v-chip>
                  <v-chip
                      small
                      label
                      filter
                      class="me-1"
                      color="primary"
                      :value="1"
                  >
                    {{ $t("Order.grantedStatus.granted") }}
                  </v-chip>
                </v-chip-group>
              </v-sheet>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.return_type"
                  :label="$t('returnPurchase.Fields.type')"
                  :items="$consts.return_purchase.type"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="returnPurchases"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>

          <template v-slot:item.purchase_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.purchase_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.hope_delivery_date="{ item }">
            <span>{{ item.hope_delivery_date | formatDate }}</span>
          </template>
          <template v-slot:item.complete_date="{ item }">
            {{ item.complete_date | formatTime }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | returnPurchaseStatusColor"
              :text-color="item.status | returnPurchaseStatusTextColor"
              >{{ item.status | returnPurchaseStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.return_type="{ item }">
            <v-chip
              small
              label
              :color="item.return_type | returnPurchaseTypesColor"
              :text-color="item.return_type | returnPurchaseTypesTextColor"
              >{{ item.return_type | returnPurchaseTypesText }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <ReturnPurchaseActions
              :purchase="item"
              showView
              :showEdit="!$route.path.startsWith('/return-purchases-approve')"
              :showConfirm="!$route.path.startsWith('/return-purchases-')"
              :showApprove="$route.path.startsWith('/return-purchases-approve')"
              showCancel
              showPrint
              showExport
              @done="getPurchasesFromAPI()"
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
<!--    &lt;!&ndash; 批量通过 &ndash;&gt;-->
<!--    <DialogAlert-->
<!--      v-model="batchApproveDialog"-->
<!--      :title="$t('$vuetify.warning')"-->
<!--      :message="$t('returnPurchase.messages.batchApproveAlert')"-->
<!--      persistent-->
<!--      useConfirm-->
<!--      @confirm="-->
<!--        batchApproveDialog = false;-->
<!--        batchApproveInfoDialog = true;-->
<!--        batchApprove(JSON.parse(JSON.stringify(options)));-->
<!--      "-->
<!--    />-->

<!--    &lt;!&ndash; 批量确认 &ndash;&gt;-->
<!--    <DialogAlert-->
<!--      v-model="batchConfirmDialog"-->
<!--      :title="$t('$vuetify.warning')"-->
<!--      :message="$t('returnPurchase.messages.batchConfirmAlert')"-->
<!--      persistent-->
<!--      useConfirm-->
<!--      @confirm="-->
<!--        batchConfirmDialog = false;-->
<!--        batchConfirmInfoDialog = true;-->
<!--        batchConfirm(JSON.parse(JSON.stringify(options)));-->
<!--      "-->
<!--    />-->


  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherStocks from "@/components/plugins/SearcherStocks.vue";
import SearcherSuppliers from "@/components/plugins/SearcherSuppliers.vue";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import ReturnPurchaseActions from "./plugins/Actions";
import ReturnPurchases from "@/api/return_purchases";
// import DialogAlert from "@/components/plugins/DialogAlert";


const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  complete_time_from: "",
  complete_time_to: "",
  return_purchase_code: "",
  org_stock_in_code: "",
  material_ids: [],
  stock_ids: [],
  supplier_ids: [],
  status: [1, 2, 3, 4],
  return_type: [1, 2],
};


function getPageType() {
  return document.URL.endsWith("/return-purchases-approve") ? 2 : 1;
}
function getOptionPrefix() {
  switch (getPageType()) {
    default:
    case 1:
      return "return-purchases";
    case 2:
      return "return-purchases-approve";
  }
}
function getOptionSelects() {
  switch (getPageType()) {
    default:
    case 1:
    case 2:
      return [
        "stocksSelected",
        "supplierSelected",
      ];
  }
}


export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherKeyword,
    SearcherStocks,
    SearcherSuppliers,
    SearcherStatus,
    ReturnPurchaseActions,
    SearcherMaterials,
    // DialogAlert,
  },

  computed: {
    items() {
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.returnPurchaseManagement"),
          disabled: false,
          href: "/return-purchases",
        },
        {
          text: i18n.t("Nav.returnPurchases"),
          disabled: true,
          href: "/return-purchases",
        },
      ];
      return items;
    },
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("returnPurchase.Fields.created_time"),
      },
      {
        key: "complete",
        text: i18n.t("returnPurchase.Fields.complete_time"),
      },
    ],
    headers() {
      var headers = [];
      headers.push(
        {
          text: i18n.t("returnPurchase.Fields.return_purchase_code"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "purchase_code",
        },
          {
            text: i18n.t("returnPurchase.Fields.stock_in_code"),
            align: "start",
            class: "text-no-wrap",
            sortable: false,
            value: "relation_stock_in_order.stock_order_code",
          },
        {
          text: i18n.t("PurchasesList.Fields.stock_name"),
          align: "start",
          class: "text-no-wrap",
          width: "100",
          sortable: false,
          value: "stock.name",
        },
        {
          text: i18n.t("PurchasesList.Fields.supplier_name"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "supplier.supplier_name",
        },
        {
          text: i18n.t("returnPurchase.Fields.type"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "return_type",
        },
        {
          text: i18n.t("PurchasesList.Fields.complete_date"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "complete_date",
        },
      );
      headers.push(
        {
          text: i18n.t("PurchasesList.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "actions",
        }
      )
      return headers;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions(getOptionPrefix(), getOptionSelects())
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "stocksSelected":
                this.stocksSelected = selected.value;
                break;
              case "supplierSelected":
                this.supplierSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  data: () => ({
    returnPurchaseCode: "",
    extCodeKeyword: "",
    districtKeyword: "",
    stocksSelected: [],
    supplierSelected: [],
    grantedStatus: [0],
    options: JSON.parse(
      sessionStorage.getItem(getOptionPrefix() + "-options")
        ? sessionStorage.getItem(getOptionPrefix() + "-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    returnPurchases: []
  }),

  watch: {
    $route: {
      handler() {
        this.pageType = getPageType();
        this.$commonUtils
          .loadOptions(getOptionPrefix(), getOptionSelects()).then(
          function (data) {
            if (data.options) {
              this.options = data.options;
            } else this.resetSearch();
            data.selecteds.forEach((selected) => {
              switch (selected.key) {
                case "stocksSelected":
                  this.stocksSelected = selected.value;
                  break;
                case "supplierSelected":
                  this.supplierSelected = selected.value;
                  break;
              }
            });
          }.bind(this)
        );
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getPurchasesFromAPI();
        sessionStorage.setItem(
          getOptionPrefix() + "-options",
          JSON.stringify(this.options)
        );
      },
    },
    stocksSelected: {
      handler() {
        this.options.stock_ids = this.stocksSelected
          ? this.stocksSelected.map((stock) => {
              return stock.id;
            })
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-stocksSelected",
          JSON.stringify(this.stocksSelected)
        );
      },
    },
    supplierSelected: {
      handler() {
        this.options.supplier_ids = this.supplierSelected
          ? this.supplierSelected.map((supplier) => {
              return supplier.id;
            })
          : [];
        sessionStorage.setItem(
          getOptionPrefix() + "-supplierSelected",
          JSON.stringify(this.supplierSelected)
        );
      },
    },
    grantedStatus: {
      handler() {
        this.getPurchasesFromAPI();
      }
    }
  },

  methods: {
    resetSearch() {
      this.stocksSelected = [];
      this.supplierSelected = [];
      this.grantedStatus = [0];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [
        this.$refs.return_purchase_code,
        this.$refs.org_stock_in_code,
        this.$refs.ext_code,
      ].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        return_purchase_code: this.returnPurchaseCode,
        ext_code: this.extCodeKeyword,
      });
    },
    getPurchasesFromAPI() {
      let options = JSON.parse(JSON.stringify(this.options));
      if (
          getPageType() == 2 &&
          this.grantedStatus &&
          Array.isArray(this.grantedStatus)
      ) {
        var status = [];
        this.grantedStatus.forEach((value) => {
          switch (value) {
            case 0:
              status.push(2);
              break;
            case 1:
              status.push(3, 4);
              break;
          }
        });
        options.status = status;
      }
      ReturnPurchases.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.returnPurchases = response.data.return_purchases;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
  },
};
</script>
